import React, { useCallback, useMemo, useState, useEffect } from 'react'
import isHotkey from 'is-hotkey'
import {
  Editable,
  withReact,
  useSlate,
  Slate,
  useSlateStatic,
  useSelected,
  useFocused,
  ReactEditor,
} from 'slate-react'
import {
  Editor,
  Transforms,
  createEditor,
  Element as SlateElement,
  Text,
  Point,
  Range,
  Path,
} from 'slate'
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { withHistory } from 'slate-history'
import { Button, Icon, Toolbar } from './RichTextComp'
import isUrl from 'is-url'
import imageExtensions from 'image-extensions'
import { css } from '@emotion/css'
import escapeHtml from 'escape-html'
import { Typography, Grid2, IconButton, Modal, Box } from '@mui/material'
//import RichTextAddEntryPopup from './RichTextAddEntryPopup'
import AddExistingEntryPopup from '../AddExistingEntryPopup'
import { useSelector } from 'react-redux'
import DeleteIcon from '../../assets/images/icons/delete_icon.svg'
import ModalBox from '../../themes/overrides/ModalBox'
import TextFieldInput from '../../themes/overrides/TextFieldInput'
import AddButton from '../../themes/overrides/AddButton'
import EditIcon from '../icons/EditIcon'
import PropTypes from 'prop-types';
import OutlinedAddButton from '../../themes/overrides/OutlinedAddButton';

const HOTKEYS = {
  'mod+b': 'bold',
  'mod+i': 'italic',
  'mod+u': 'underline',
  'mod+`': 'code',
  'mod+a': 'code',
}
const LIST_TYPES = ['numbered-list', 'bulleted-list']
const TEXT_ALIGN_TYPES = ['left', 'center', 'right', 'justify']
const RichText = ({ onChange, value, locale, disabled }) => {
  const renderElement = useCallback(props => <Element {...props} />, [])
  const renderLeaf = useCallback(props => <Leaf {...props} />, [])
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // const editor = useMemo(() => withHistory(withReact(createEditor())), [])
  const editor = useMemo(
    () => withTables(withImages(withHistory(withReact(createEditor())))),
    []
  );

  const handleChange = () => {
    if (onChange) {
      onChange(editor.children); // Pass the serialized value to the parent
    }
  };

  const initialValue = value ? value : [
    {
      type: 'paragraph',
      children: [
        { text: '' }
      ],
    },

  ];


  return (
    <Box sx={{
      height: 400,
      borderRadius: 2,
      borderColor: "#ccc",
      borderWidth: '1px',
      overflowY: {
        xs: 'unset',
        md: 'auto'
      },
    }}
    >
      <Slate editor={editor} initialValue={initialValue} onChange={handleChange} >
        {!disabled && (
          <Toolbar style={{ padding: '10px 20px', display: { xs: 'block', md: 'flex' }, justifyContent: { md: 'space-between' }, borderRadius: '10px 10px 0px 0px' }}>
            <MarkButton format="bold" icon="format_bold" />
            <MarkButton format="italic" icon="format_italic" />
            <MarkButton format="underline" icon="format_underlined" />
            <MarkButton format="code" icon="code" />
            <BlockButton format="heading-one" icon="looks_one" />
            <BlockButton format="heading-two" icon="looks_two" />
            <BlockButton format="block-quote" icon="format_quote" />
            <BlockButton format="numbered-list" icon="format_list_numbered" />
            <BlockButton format="bulleted-list" icon="format_list_bulleted" />
            <BlockButton format="left" icon="format_align_left" />
            <BlockButton format="center" icon="format_align_center" />
            <BlockButton format="right" icon="format_align_right" />
            <BlockButton format="justify" icon="format_align_justify" />
            <InsertImageButton />
            <InsertURLImageButton />
            <InsertTableButton locale={locale} />
            <EmbedButton />
          </Toolbar>
        )}
        <Editable
          // renderElement={props => <Element {...props} />} // Render elements
          renderElement={(props) => renderElement({ ...props, locale })}
          renderLeaf={renderLeaf} // Render leaves
          placeholder="..."
          spellCheck
          readOnly={disabled}
          style={{
            height: '400px', overflowY: 'auto', outline: 'none', padding: '5px'
          }}
          // autoFocus
          onKeyDown={event => {
            // Handle mod+a hotkey
            if (isHotkey('mod+a', event)) {
              event.preventDefault();
              Transforms.select(editor, []);
              return;
            }

            // Handle dynamic HOTKEYS
            for (const hotkey in HOTKEYS) {
              if (isHotkey(hotkey, event)) {
                event.preventDefault();
                const mark = HOTKEYS[hotkey];
                toggleMark(editor, mark);
              }
            }
          }}
        />
      </Slate>
    </Box>
  )
}
const toggleBlock = (editor, format) => {
  const isActive = isBlockActive(
    editor,
    format,
    TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type'
  )
  const isList = LIST_TYPES.includes(format)
  Transforms.unwrapNodes(editor, {
    match: n =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      LIST_TYPES.includes(n.type) &&
      !TEXT_ALIGN_TYPES.includes(format),
    split: true,
  })
  let newProperties
  if (TEXT_ALIGN_TYPES.includes(format)) {
    newProperties = {
      align: isActive ? undefined : format,
    }
  } else {
    newProperties = {
      type: isActive ? 'paragraph' : isList ? 'list-item' : format,
    }
  }
  Transforms.setNodes(editor, newProperties)
  if (!isActive && isList) {
    const block = { type: format, children: [] }
    Transforms.wrapNodes(editor, block)
  }
}
const toggleMark = (editor, format) => {
  const isActive = isMarkActive(editor, format)
  if (isActive) {
    Editor.removeMark(editor, format)
  } else {
    Editor.addMark(editor, format, true)
  }
}
const isBlockActive = (editor, format, blockType = 'type') => {
  const { selection } = editor
  if (!selection) return false
  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: n =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        n[blockType] === format,
    })
  )
  return !!match
}
const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor)
  return marks ? marks[format] === true : false
}
const Element = props => {

  const { attributes, children, element, locale } = props
  const editor = useSlateStatic()
  const path = ReactEditor.findPath(editor, element)
  const selected = useSelected()
  const focused = useFocused()
  const style = { textAlign: element.align }
  switch (element.type) {
    case 'block-quote':
      return (
        <blockquote style={style} {...attributes}>
          {children}
        </blockquote>
      )
    case 'bulleted-list':
      return (
        <ul style={style} {...attributes}>
          {children}
        </ul>
      )
    case 'heading-one':
      return (
        <h1 style={style} {...attributes}>
          {children}
        </h1>
      )
    case 'heading-two':
      return (
        <h2 style={style} {...attributes}>
          {children}
        </h2>
      )
    case 'list-item':
      return (
        <li style={style} {...attributes}>
          {children}
        </li>
      )
    case 'numbered-list':
      return (
        <ol style={style} {...attributes}>
          {children}
        </ol>
      )
    case 'image':
      return <Image {...props} />
    case 'entry':
      return <EmbedEntry {...props} />
    case 'table':
      return (
        <TableRender attributes={attributes} element={element} children={children} locale={locale} />
      )
    case 'table-row':
      return <tr {...attributes}>{children}</tr>
    case 'table-cell':
      return (
        <td
          {...attributes}
          style={{
            border: element.borderSize === 0 ? 'none' : `${element.borderSize}px solid gray`,
            padding: '5px',
          }}
        >
          {children}</td>
      );
    default:
      return (
        <p style={style} {...attributes}>
          {children}
        </p>
      )
  }
}
const Leaf = ({ attributes, children, leaf }) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>
  }
  if (leaf.code) {
    children = <code>{children}</code>
  }
  if (leaf.italic) {
    children = <em>{children}</em>
  }
  if (leaf.underline) {
    children = <u>{children}</u>
  }
  return <span {...attributes}>{children}</span>
}
const BlockButton = ({ format, icon }) => {
  const editor = useSlate()
  return (
    <Button
      active={isBlockActive(
        editor,
        format,
        TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type'
      )}
      onMouseDown={event => {
        event.preventDefault()
        toggleBlock(editor, format)
      }}
    >
      <Icon>{icon}</Icon>
    </Button>
  )
}
const MarkButton = ({ format, icon }) => {
  const editor = useSlate()
  return (
    <Button
      active={isMarkActive(editor, format)}
      onMouseDown={event => {
        event.preventDefault()
        toggleMark(editor, format)
      }}
    >
      <Icon>{icon}</Icon>
    </Button>
  )
}

const withImages = editor => {
  const { insertData, isVoid } = editor
  editor.isVoid = element => {
    return element.type === 'image' ? true : isVoid(element)
  }
  editor.insertData = data => {
    const text = data.getData('text/plain')
    const { files } = data
    if (files && files.length > 0) {
      for (const file of files) {
        const reader = new FileReader()
        const [mime] = file.type.split('/')
        if (mime === 'image') {
          reader.addEventListener('load', () => {
            const url = reader.result
            insertImage(editor, url)
          })
          reader.readAsDataURL(file)
        }
      }
    } else if (isImageUrl(text)) {
      insertImage(editor, text)
    } else {
      insertData(data)
    }
  }
  return editor
}
const insertImage = (editor, url) => {
  const text = { text: '' }
  const image = { type: 'image', url, children: [text] }
  Transforms.insertNodes(editor, image)
  Transforms.insertNodes(editor, {
    type: 'paragraph',
    children: [{ text: '' }],
  })
}

const handleEditImage = (editor, path, element) => {
  const alt = prompt("Enter alt text:", element.alt || "");
  console.log(alt);
  console.log(path);
  if (alt !== null) {
    Transforms.setNodes(
      editor,
      { alt },
      { at: path }
    );
  }
};


const Image = ({ attributes, children, element }) => {
  const editor = useSlateStatic()
  const path = ReactEditor.findPath(editor, element)
  const selected = useSelected()
  const focused = useFocused()
  return (
    <div {...attributes}>
      {children}
      <div
        contentEditable={false}
        className={css`
          position: relative;
        `}
      >
        <img
          src={element.url}
          className={css`
            display: block;
            max-width: 100%;
            max-height: 20em;
            box-shadow: ${selected && focused ? '0 0 0 3px #B4D5FF' : 'none'};
          `}
        />
        <Button
          active
          onClick={() => Transforms.removeNodes(editor, { at: path })}
          className={css`
            display: ${selected && focused ? 'inline' : 'none'};
            position: absolute;
            top: 0.5em;
            left: 0.5em;
            background-color: white;
          `}
        >
          <Icon>delete</Icon>
        </Button>

        <Button
          active
          onClick={() => handleEditImage(editor, path, element)}
          className={css`
            display: ${selected && focused ? 'inline' : 'none'};
            position: absolute;
            top: 0.5em;
            right: 0.5em;
            background-color: white;
          `}
        >
          <Icon>edit</Icon>
        </Button>
      </div>
    </div>
  )
}



const EmbedEntry = ({ attributes, children, element }) => {
  const editor = useSlateStatic()
  const path = ReactEditor.findPath(editor, element)
  const selected = useSelected()
  const focused = useFocused()
  return (
    <p {...attributes} contentEditable={false} >
      <div style={{ position: 'relative' }}>
        <Grid2 container sx={{
          border: '1.5px solid #ECECEC',
          borderRadius: 1,
          padding: '8px',
          margin: "0px 8px"
        }}>
          <Grid2 size={9}>
            <Typography variant="h6" component="h2" sx={{
              fontSize: '18px',
            }}>
              {element.identifier}
            </Typography>
          </Grid2>
          <Grid2 size={3}
            textAlign='right'>
            {element.published === true ? (
              <Typography
                sx={{
                  color: 'green',
                  backgroundColor: '#e6ffe6',
                  display: 'inline-block',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: 13,
                }}
              >
                Published
              </Typography>
            ) : (
              <Typography
                sx={{
                  color: '#856404',
                  backgroundColor: '#fff3cd',
                  display: 'inline-block',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: 13,
                }}
              >
                Draft
              </Typography>
            )}
            <IconButton
              onClick={() => {
                const path = ReactEditor.findPath(editor, element);
                Transforms.removeNodes(editor, { at: path });
              }}
            >
              <img
                src={DeleteIcon}
                alt="delete icon"
                style={{ width: '20px', height: '20px' }}
              />
            </IconButton>
          </Grid2>
        </Grid2>
        {children}
      </div>
    </p >
  )
}

//Can use for URL upload
//Images
const InsertURLImageButton = () => {
  const editor = useSlateStatic()
  return (
    <Button
      onMouseDown={event => {
        event.preventDefault()
        const url = window.prompt('Enter the URL of the image:')
        if (url && !isImageUrl(url)) {
          alert('URL is not an image')
          return
        }
        url && insertImage(editor, url)
      }}
    >
      <Icon>add_photo_alternate</Icon>
    </Button>
  )
}

const TableRender = ({ attributes, element, children, locale }) => {
  const editor = useSlateStatic()
  const path = ReactEditor.findPath(editor, element)
  const selected = useSelected()
  const focused = useFocused()

  const handleEdit = () => {
    const [maybeTable] = Editor.nodes(editor, {
      match: n => n.type === 'table',
      at: path,
    })
    if (maybeTable) {
      const tableButton = document.querySelector(`.table-button-${locale}`)
      if (tableButton) {
        const customEvent = new CustomEvent('edit-table', {
          bubbles: true,
          detail: {
            tableData: maybeTable,
            locale
          }
        })
        tableButton.dispatchEvent(customEvent)
      }
    }
  }

  return (
    <div style={{ position: 'relative' }}>
      <Button
        style={{
          display: selected && focused ? 'flex' : 'none',
          position: 'absolute',
          top: '-28px',
          right: 0,
          padding: '4px',
          minWidth: 'auto',
          border: '1px solid #ddd',
          borderRadius: '4px',
          zIndex: 1,
        }}
        onClick={handleEdit}
      >
        <Icon>edit</Icon>
      </Button>
      <table
        {...attributes}
        style={{
          width: element.width || '100%',
          height: element.height || 'auto',
          border: element.borderSize === 0 ? 'none' : `${element.borderSize || 1}px solid gray`,
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
        }}
      >
        <tbody>{children}</tbody>
      </table>
    </div>
  )
}



const InsertTableButton = ({ locale }) => {
  const editor = useSlateStatic();
  const [showModal, setShowModal] = useState(false);
  const [columns, setColumns] = useState(2);
  const [rows, setRows] = useState(2);
  const [width, setWidth] = useState('100%');
  const [height, setHeight] = useState('auto');
  const [borderSize, setBorderSize] = useState(1);
  const [editingTable, setEditingTable] = useState(false);
  const [columnError, setColumnError] = useState('');
  const [rowError, setRowError] = useState('');

  useEffect(() => {
    const button = document.querySelector(`.table-button-${locale}[data-table-button="${locale}"]`)

    const handleEditTable = (e) => {
      const tableData = e.detail?.tableData
      const eventLocale = e.detail?.locale

      if (tableData && eventLocale === locale) {
        setEditingTable(tableData)
        const node = tableData[0]
        setColumns(node.columns || 2)
        setRows(node.children ? node.children.length : 2)
        setWidth(node.width || '100%')
        setHeight(node.height || 'auto')
        setBorderSize(node.borderSize || 1)
        setShowModal(true)
      }
    }

    if (button) {
      button.addEventListener('edit-table', handleEditTable)
      return () => button.removeEventListener('edit-table', handleEditTable)
    }
  }, [locale])



  const insertTable = (editor, columns, rows, width, height, borderSize) => {
    const table = {
      type: 'table',
      width,
      height,
      borderSize,
      columns,
      children: [],
    };
    for (let r = 0; r < rows; r++) {
      const tableRow = {
        type: 'table-row',
        children: [],
      };
      for (let c = 0; c < columns; c++) {
        tableRow.children.push({
          type: 'table-cell',
          borderSize,
          children: [{ text: '' }],
        });
      }
      table.children.push(tableRow);
    }
    Transforms.insertNodes(editor, [
      table,
      {
        type: 'paragraph',
        children: [{ text: '' }],
      },
    ]);
  };

  const editSelectedTable = () => {
    if (!editingTable) return;

    const [existingTable, path] = editingTable;
    const existingRows = existingTable.children || [];
    const updatedChildren = [];

    for (let r = 0; r < rows; r++) {
      if (r < existingRows.length) {
        const existingCells = existingRows[r].children || [];
        const newCells = [];

        for (let c = 0; c < columns; c++) {
          if (c < existingCells.length) {
            newCells.push({
              ...existingCells[c],
              borderSize
            });
          } else {
            newCells.push({
              type: 'table-cell',
              borderSize,
              children: [{ text: '' }]
            });
          }
        }

        updatedChildren.push({
          type: 'table-row',
          children: newCells.slice(0, columns)
        });
      } else {
        const newRow = {
          type: 'table-row',
          children: Array(columns).fill(null).map(() => ({
            type: 'table-cell',
            borderSize,
            children: [{ text: '' }]
          }))
        };
        updatedChildren.push(newRow);
      }
    }

    Transforms.removeNodes(editor, {
      at: path,
      match: n => n.type === 'table'
    });

    Transforms.insertNodes(
      editor,
      {
        type: 'table',
        width,
        height,
        borderSize,
        columns,
        children: updatedChildren
      },
      { at: path }
    );

    setShowModal(false);
  };

  const isValid = () => {
    return columns >= 1 && rows >= 1;
  };

  const handleCreateOrEdit = () => {
    if (!isValid()) return;

    if (editingTable) {
      editSelectedTable();
    } else {
      insertTable(editor, columns, rows, width, height, borderSize);
      setShowModal(false);
    }
  };

  const handleColumnChange = (e) => {
    const value = parseInt(e.target.value);
    setColumns(value);
    setColumnError(value < 1 ? 'Minimum value is 1' : '');
  };

  const handleRowChange = (e) => {
    const value = parseInt(e.target.value);
    setRows(value);
    setRowError(value < 1 ? 'Minimum value is 1' : '');
  };

  const resetToDefaults = () => {
    setColumns(2);
    setRows(2);
    setWidth('100%');
    setHeight('auto');
    setBorderSize(1);
    setColumnError('');
    setRowError('');
    setEditingTable(null);
  };

  const handleCloseModal = () => {
    resetToDefaults();
    setShowModal(false);
  };


  return (
    <div>
      <Button
        className={`table-button-${locale}`}
        data-table-button={locale}
        onMouseDown={(e) => {
          e.preventDefault()
          console.log('Table button clicked for locale:', locale);
          if (e.type === 'mousedown' && !e.detail?.tableData) {
            resetToDefaults()
            setShowModal(true)
          }
        }}
      >
        <label>
          <Icon style={{ cursor: 'pointer' }}>table</Icon>
        </label>
      </Button>
      <Modal open={showModal} onClose={handleCloseModal}>
        <ModalBox sx={{ width: '520px', maxWidth: '95%' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {editingTable ? 'Edit Table' : 'Create Table'}
          </Typography>
          <Grid2 container spacing={2}>
            <Grid2 size={6}>
              <TextFieldInput
                label='Columns'
                type='number'
                value={columns}
                onChange={handleColumnChange}
                error={!!columnError}
              />
              {columnError && (
                <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                  {columnError}
                </Typography>
              )}
            </Grid2>
            <Grid2 size={6}>
              <TextFieldInput
                label='Rows'
                type='number'
                value={rows}
                onChange={handleRowChange}
                error={!!rowError}
              />
              {rowError && (
                <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                  {rowError}
                </Typography>
              )}
            </Grid2>
            <Grid2 size={4}>
              <TextFieldInput
                type='text'
                label='Width'
                value={width}
                onChange={e => setWidth(e.target.value)}
              />
            </Grid2>
            <Grid2 size={4}>
              <TextFieldInput
                type='text'
                label='Height'
                value={height}
                onChange={e => setHeight(e.target.value)}
              />
            </Grid2>
            <Grid2 size={4}>
              <TextFieldInput
                label='Border Size'
                type='number'
                value={borderSize}
                onChange={e => setBorderSize(parseInt(e.target.value))}
              />
            </Grid2>
          </Grid2>
          <Box justifyContent='right' display='flex' margin="10px 0px" gap={1}>
            <OutlinedAddButton
              startIcon={null}
              label='Cancel'
              variant='outlined'
              onClick={handleCloseModal}
            />
            <AddButton
              startIcon={null}
              label={editingTable ? 'Save' : 'Create'}
              onClick={handleCreateOrEdit}
              disabled={!isValid()}
            />
          </Box>
        </ModalBox>
      </Modal>
    </div>
  );
};
// const withTables = (editor) => {
//   const { isVoid, isInline } = editor;

//   editor.isVoid = (element) =>
//       element.type === 'table' || element.type === 'table-row' || element.type === 'table-cell'
//           ? false
//           : isVoid(element);

//   editor.isInline = (element) => (element.type === 'table-cell' ? true : isInline(element));

//   return editor;
// };


const withTables = editor => {
  const { deleteBackward, deleteForward, insertBreak } = editor
  editor.deleteBackward = unit => {
    const { selection } = editor
    if (selection && Range.isCollapsed(selection)) {
      const [cell] = Editor.nodes(editor, {
        match: n =>
          !Editor.isEditor(n) &&
          SlateElement.isElement(n) &&
          n.type === 'table-cell',
      })
      if (cell) {
        const [, cellPath] = cell
        const start = Editor.start(editor, cellPath)
        if (Point.equals(selection.anchor, start)) {
          return
        }
      }
    }
    deleteBackward(unit)
  }
  editor.deleteForward = unit => {
    const { selection } = editor
    if (selection && Range.isCollapsed(selection)) {
      const [cell] = Editor.nodes(editor, {
        match: n =>
          !Editor.isEditor(n) &&
          SlateElement.isElement(n) &&
          n.type === 'table-cell',
      })
      if (cell) {
        const [, cellPath] = cell
        const end = Editor.end(editor, cellPath)
        if (Point.equals(selection.anchor, end)) {
          return
        }
      }
    }
    deleteForward(unit)
  }
  editor.insertBreak = () => {
    const { selection } = editor
    if (selection) {
      const [table] = Editor.nodes(editor, {
        match: n =>
          !Editor.isEditor(n) &&
          SlateElement.isElement(n) &&
          n.type === 'table',
      })
      if (table) {
        return
      }
    }
    insertBreak()
  }
  return editor
}


//Can use for image upload to rich Text editor
const InsertImageButton = () => {
  const editor = useSlateStatic()

  const handleImageUpload = async (file) => {

    const token = localStorage.getItem('token');
    const workspaceID = localStorage.getItem('workspaceID');


    const formData = new FormData()
    formData.append('file', file)
    formData.append('workspaceId', workspaceID)

    const headers = new Headers();
    headers.append('Authorization', `Bearer ${token}`);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/dashboard/rich-text/upload`, {
        method: 'POST',
        body: formData,
        headers: headers,
      })
      const data = await response.json()
      if (data && data.url) {
        insertImage(editor, data.url)
      } else {
        alert('Failed to upload image')
      }
    } catch (error) {
      console.error('Image upload failed:', error)
      alert('Image upload failed')
    }
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (file && file.type.startsWith('image/')) {
      handleImageUpload(file)
    } else {
      alert('Please select a valid image file')
    }
  }

  return (
    <Button>
      <label>
        <Icon style={{ cursor: 'pointer' }}>image</Icon>
        <input
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </label>
    </Button>
  )
}

const EmbedButton = () => {
  const [showMenu, setShowMenu] = useState(false);
  const [addExistingEntryPopup, setAddExistingEntryPopup] = useState(false);
  const editor = useSlateStatic();
  const entries = useSelector((state) => state.entry.entries) || [];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleRichTextAddEntry = (selectedEntriesIds) => {
    setShowMenu(false);
    setAddExistingEntryPopup(false);

    const selectedEntries = entries.filter(entry =>
      selectedEntriesIds.includes(entry._id)
    )

    selectedEntries.forEach((entry, index) => {
      const entryNode = {
        type: 'entry',
        entryId: entry._id,
        identifier: entry.identifier,
        published: entry.published,
        children: [{ text: '' }]
      }

      Transforms.insertNodes(editor, entryNode);

      if (index === selectedEntries.length - 1) {
        Transforms.insertNodes(editor, {
          type: 'paragraph',
          children: [{ text: '' }]
        })
      }
    });
  }

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <Button
        onMouseDown={(e) => {
          e.preventDefault();
          setShowMenu(prevState => !prevState);
        }}
        className="border border-gray-500 mt-1.5 rounded-md text-center justify-center flex gap-2"
      >
        <Icon className="text-gray-500">add</Icon>
        <span className="text-gray-500 text-sm">Embed</span>
        <Icon
          className={`text-gray-500 transition-transform pt-0.5 ${showMenu ? "rotate-0" : "rotate-180"
            }`}
        >
          arrow_drop_down
        </Icon>
      </Button>

      {showMenu && (
        <Button
          onMouseDown={() => {
            setAddExistingEntryPopup(true);
          }}
        >
          <div
            className="absolute z-10 top-full mt-0.5 w-full py-1 text-sm bg-white border border-gray-300 rounded-md shadow-md hover:bg-gray-100">
            <span className='text-gray-500 ml-2'>Entry</span>

          </div>
        </Button>
      )}
      <AddExistingEntryPopup
        open={addExistingEntryPopup}
        onClose={() => setAddExistingEntryPopup(false)}
        onConfirm={handleRichTextAddEntry}
      />
    </div>
  );
};


const isImageUrl = url => {
  if (!url) return false
  if (!isUrl(url)) return false
  const ext = new URL(url).pathname.split('.').pop()
  return imageExtensions.includes(ext)
}

RichText.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.any,
  locale: PropTypes.any
};

export default RichText